import { SelectField } from '@atlas/ui-components'
import { Switch } from '@atlas/ui-primitives'
import { CommaListInput } from '@/components/prompts/CommaListInput'
import { ItemTemplateInput } from '@/components/prompts/ItemTemplateInput'
import { JoinControl } from '@/components/prompts/PromptJoinControl'
import type { PromptListRecord, PromptVariable } from '@/domain/prompt.types'
import { PromptVariableType } from '@/domain/prompt.types'

/**
 * The extra control a variable's TYPE demands: a list picker for `list`, an options
 * field for `enum`, nothing for the scalar types.
 */
export function TypeSpecificControls({
	variable,
	labelPrefix,
	lists,
	siblings,
	isDisabled,
	onPatch,
}: {
	variable: PromptVariable
	labelPrefix: string
	lists: PromptListRecord[]
	/** Every other variable, so a `join` can name the ones it composes. */
	siblings: PromptVariable[]
	isDisabled: boolean
	onPatch: (next: Partial<PromptVariable>) => void
}) {
	if (variable.type === PromptVariableType.JOIN) {
		return (
			<JoinControl
				variable={variable}
				labelPrefix={labelPrefix}
				siblings={siblings}
				isDisabled={isDisabled}
				onPatch={onPatch}
			/>
		)
	}

	if (variable.type === PromptVariableType.LIST) {
		return (
			<>
				<SelectField
					label="Shared list"
					// The one picker here whose options are DATA: a tenant's list count grows,
					// unlike the fixed handful of variable types.
					isSearchable
					ariaLabel={`${labelPrefix} list`}
					value={variable.listCode ?? ''}
					isDisabled={isDisabled}
					placeholder="Select a list…"
					options={lists.map((l) => ({ value: l.code, label: l.name }))}
					onChange={(value) => onPatch({ listCode: value })}
				/>
				<MultipleToggle variable={variable} isDisabled={isDisabled} onPatch={onPatch} />
			</>
		)
	}

	if (variable.type === PromptVariableType.EACH) {
		return <EachControls variable={variable} labelPrefix={labelPrefix} isDisabled={isDisabled} onPatch={onPatch} />
	}
	if (variable.type === PromptVariableType.ENUM) {
		return (
			<>
				<CommaListInput
					label="Options (comma-separated)"
					ariaLabel={`${labelPrefix} options`}
					value={variable.options ?? []}
					isDisabled={isDisabled}
					onChange={(options) => onPatch({ options })}
				/>
				<MultipleToggle variable={variable} isDisabled={isDisabled} onPatch={onPatch} />
			</>
		)
	}

	return null
}

/**
 * Whether this variable takes SEVERAL of its options.
 *
 * Only on `list` and `enum`, the two types with options to choose several of. It
 * exists because `multiple` was readable and unsettable: the preview form renders a
 * `MultiCombobox` for it, but no control anywhere turned it on, so the only way to
 * reach that branch was to hand-edit the stored JSON.
 */
function MultipleToggle({
	variable,
	isDisabled,
	onPatch,
}: {
	variable: PromptVariable
	isDisabled: boolean
	onPatch: (next: Partial<PromptVariable>) => void
}) {
	return (
		<div className="mt-2">
			<Switch
				isSelected={variable.multiple === true}
				isDisabled={isDisabled}
				onChange={(multiple) => onPatch({ multiple })}
			>
				Allow several values
			</Switch>
		</div>
	)
}

/**
 * An `each` variable's contract: the per-item template, and the fields every item
 * is expected to carry.
 *
 * Split out because it is the largest of the type branches — declaring the fields is
 * what lets a save reject a template referring to one that was never promised.
 */
function EachControls({
	variable,
	labelPrefix,
	isDisabled,
	onPatch,
}: {
	variable: PromptVariable
	labelPrefix: string
	isDisabled: boolean
	onPatch: (next: Partial<PromptVariable>) => void
}) {
	return (
		<div className="space-y-2">
			<div>
				<CommaListInput
					label="Item fields (comma-separated)"
					ariaLabel={`${labelPrefix} item fields`}
					value={variable.itemFields ?? []}
					placeholder="severity, statement, checkDefinition"
					isDisabled={isDisabled}
					// Cleared back to undefined rather than an empty array: absent means
					// "unchecked", while `[]` would claim the items have no fields at all.
					onChange={(fields) => onPatch({ itemFields: fields.length > 0 ? fields : undefined })}
				/>
				<p className="mt-1 text-ds-2xs text-ds-text-subtle">
					The shape callers must supply. Declared, a template field nothing promised is refused on save instead of
					failing mid-render.
				</p>
			</div>
			<label className="block">
				<span className="mb-1 block text-ds-2xs text-ds-text-subtle">
					Item template — {'{{field}}'} refers to each item's fields
				</span>
				<ItemTemplateInput
					value={variable.itemTemplate ?? ''}
					// Completes over the fields declared ABOVE, which is what makes the two
					// halves of the contract visible as one.
					fields={variable.itemFields ?? []}
					ariaLabel={`${labelPrefix} item template`}
					placeholder="- [{{severity}}] {{statement}}"
					isDisabled={isDisabled}
					onChange={(itemTemplate) => onPatch({ itemTemplate })}
				/>
			</label>
		</div>
	)
}
